import {
  AloftPrice,
  EditableSubjectData,
  RegressionOutput,
  UnitRateKey,
  UnitRateModelFeatureKey,
  getRegressionFeatureErrorWarning,
  isDefined,
} from '@aloft/toolkit-shared';
import {
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  Typography,
  colors,
} from '@aloft/toolkit-sui';

import { selectEditablePropertyDataById } from '../../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

type FeatureRow = {
  label: string;
  unit: string;
  featureKey: UnitRateModelFeatureKey;
  getValue: (subject?: EditableSubjectData) => number | undefined;
};

const featureRows: FeatureRow[] = [
  {
    label: 'GLA',
    unit: 'sf',
    featureKey: 'gla',
    getValue: (s) => s?.improvements?.gla?.unitAmount,
  },
  {
    label: 'Bath (Full)',
    unit: 'ba',
    featureKey: 'fullBaths',
    getValue: (s) => s?.improvements?.fullBaths,
  },
  {
    label: 'Bath (Half)',
    unit: 'ba',
    featureKey: 'halfBaths',
    getValue: (s) => s?.improvements?.halfBaths,
  },
  {
    label: 'Bed',
    unit: 'be',
    featureKey: 'bedrooms',
    getValue: (s) => s?.improvements?.bedrooms,
  },
  {
    label: 'Parking',
    unit: 'spc',
    featureKey: 'garageCarport',
    getValue: (s) => {
      const g = s?.improvements?.garage?.spaces ?? 0;
      const dg = s?.improvements?.detachedGarage?.spaces ?? 0;
      const cp = s?.improvements?.carport?.spaces ?? 0;
      return g + dg + cp;
    },
  },
  {
    label: 'Actual Age',
    unit: 'yr',
    featureKey: 'actualAge',
    getValue: (s) => s?.improvements?.age,
  },
  {
    label: 'Site Size',
    unit: 'sf',
    featureKey: 'siteSize',
    getValue: (s) => s?.site?.area?.unitAmount,
  },
];

const StatusCell = ({
  label,
  isPositive,
  notApplicable,
}: {
  label?: string;
  isPositive: boolean;
  notApplicable?: boolean;
}) => (
  <Td backgroundColor="surface1" {...{ __dangerDoNotUseSx: { verticalAlign: 'middle' } }}>
    <Typography variant="body2" color={isPositive ? 'textSuccess' : 'textTertiary'}>
      {notApplicable ? 'N/A' : (label ?? '—')}
    </Typography>
  </Td>
);

const ConstraintPill = ({ bound, value }: { bound: 'min' | 'max'; value: number }) => (
  <Tooltip label={`Constrained to the ${bound}imum allowed rate: $${value.toLocaleString()}`} placement="top">
    <Flex
      borderRadius="999px"
      padding="2px 8px"
      backgroundColor="surface7"
      __dangerDoNotUseSx={{ cursor: 'help' }}
    >
      <Typography variant="subtitle2">{bound === 'min' ? 'Min bound' : 'Max bound'}</Typography>
    </Flex>
  </Tooltip>
);

export const RegressionModalFeatureTable = ({ regression }: { regression?: RegressionOutput }) => {
  const subject = useAppSelector((state) => selectEditablePropertyDataById(state, 'subject')) as
    | EditableSubjectData
    | undefined;

  const output = regression?.status === 'success' ? regression.output : null;
  if (!output) return null;

  const predictedPrice: AloftPrice = featureRows.reduce((total, row) => {
    const featureValue = row.getValue(subject);
    const { rate } = output.unitRates[row.featureKey];
    if (featureValue && rate) return total.add(AloftPrice.schema.parse(rate).mul(featureValue));

    return total;
  }, AloftPrice.schema.parse(output.constant));

  return (
    <Table variant="grid">
      <Thead>
        <Tr>
          <Th backgroundColor="surface7">Variable</Th>
          <Th backgroundColor="surface7">Subject Features</Th>
          <Th backgroundColor="surface7">Coefficient</Th>
          <Th backgroundColor="surface7">Price Value</Th>
          <Th backgroundColor="surface7">Predicted Sale Price</Th>
          <Th backgroundColor="surface7">Significance</Th>
          <Th backgroundColor="surface7">Confidence</Th>
        </Tr>
      </Thead>
      <Tbody>
        {featureRows.map((row, index) => {
          const result = output.unitRates[row.featureKey];
          const featureValue = row.getValue(subject);
          const warningForFeature = getRegressionFeatureErrorWarning(
            regression,
            row.featureKey as UnitRateKey,
          );

          return (
            <Tr key={row.featureKey}>
              <Td
                backgroundColor="surface2"
                {...{ __dangerDoNotUseSx: { verticalAlign: 'middle' } }}
              >
                <Flex alignItems="center" gap="0.3rem" flexDirection="row">
                  <Typography variant="body2">{row.label}</Typography>
                  {warningForFeature && (
                    <Flex
                      alignItems="center"
                      gap="0.25rem"
                      marginBottom="2px"
                      __dangerDoNotUseSx={{ cursor: 'help' }}
                    >
                      <Tooltip label={warningForFeature} placement="top">
                        <Icon
                          icon="error-warning"
                          size="sm"
                          color={result.rate ? 'textError' : 'textWarning'}
                        />
                      </Tooltip>
                    </Flex>
                  )}
                </Flex>
              </Td>
              <Td
                backgroundColor="surface1"
                {...{ __dangerDoNotUseSx: { verticalAlign: 'middle' } }}
              >
                <Typography variant="body2">
                  {isDefined(featureValue) ? `${featureValue.toFixed(2)} ${row.unit}` : '—'}
                </Typography>
              </Td>
              <Td
                backgroundColor="surface1"
                {...{ __dangerDoNotUseSx: { verticalAlign: 'middle' } }}
              >
                <Flex alignItems="center" gap="0.3rem">
                  <Typography variant="body2">
                    {result.rate
                      ? `${AloftPrice.schema.parse(result.rate).toString(true)} / ${row.unit}`
                      : '—'}
                  </Typography>
                  {result.constraint && <ConstraintPill {...result.constraint} />}
                </Flex>
              </Td>
              <Td
                backgroundColor="surface1"
                {...{ __dangerDoNotUseSx: { verticalAlign: 'middle' } }}
              >
                {result.rate && isDefined(featureValue) ? (
                  <Flex flexDirection="column" gap="0.125rem">
                    <Typography
                      variant="subtitle2"
                      __dangerDoNotUseSx={{ color: colors.textTertiary }}
                    >
                      {`${featureValue.toFixed(2)} X ${AloftPrice.schema.parse(result.rate).toString(true)} =`}
                    </Typography>
                    <Typography variant="body1">
                      {AloftPrice.schema.parse(result.rate).mul(featureValue).toString(true)}
                    </Typography>
                  </Flex>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Td>
              {index === 0 && (
                <Td
                  rowSpan={featureRows.length + 1}
                  backgroundColor="surface8"
                  textAlign="center"
                  whiteSpace="nowrap"
                  {...{ __dangerDoNotUseSx: { verticalAlign: 'middle' } }}
                >
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="0.25rem"
                  >
                    <Typography variant="h2">{predictedPrice.toString(true)}</Typography>
                    <Typography variant="body1" __dangerDoNotUseSx={{ color: colors.textTertiary }}>
                      = Constant + ∑ Price Value
                    </Typography>
                  </Flex>
                </Td>
              )}
              <StatusCell
                label={result.pValue?.label}
                isPositive={result.pValue?.label === 'Significant'}
                notApplicable={Boolean(result.constraint)}
              />
              <StatusCell
                label={result.confidenceInterval?.label}
                isPositive={result.confidenceInterval?.label === 'High'}
                notApplicable={Boolean(result.constraint)}
              />
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};
